// frontend/src/pages/AnalysisPage/sections/ColumnMapping/ColumnMapping.js
// Purpose: Display and manage column mapping between file columns and system fields
// Desired outcome: Allow users to review suggested mappings, make manual adjustments,
// and confirm final mappings before proceeding to the next workflow step

import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectCurrentFile, 
  selectExtractedColumns, 
  selectColumnMappings,
  selectProgress,
  selectFieldMappings,
  fetchSuggestedMappings,
  confirmColumnMappings,
  fetchFieldMappings,
  setError,
  clearError
} from '../../../../state/analysis/analysisSlice';
import FieldGroup from './FieldGroup';
import logger from '../../../../utils/logger';
import '../sections.css';
import './ColumnMapping.css';

const ColumnMapping = () => {
  const dispatch = useDispatch();
  const currentFile = useSelector(selectCurrentFile);
  const extractedColumns = useSelector(selectExtractedColumns);
  const columnMappings = useSelector(selectColumnMappings);
  const progress = useSelector(selectProgress);
  const fieldMappingsData = useSelector(selectFieldMappings);
  
  // Local state for modifications to mappings
  const [localMappings, setLocalMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setLocalError] = useState(null);
  const [fieldGroups, setFieldGroups] = useState([]);
  // Add a state to track if we've already fetched the suggested mappings
  const [fetchedMappings, setFetchedMappings] = useState(false);

  // Memoized function to load field mappings
  const loadFieldMappings = useCallback(async () => {
    try {
      if (!fieldMappingsData) {
        await dispatch(fetchFieldMappings()).unwrap();
        return;
      }

      if (fieldMappingsData && fieldMappingsData.fields) {
        const groups = Object.entries(fieldMappingsData.fields).map(([groupName, fields]) => ({
          name: groupName,
          fields: fields.map(field => ({
            name: field.name,
            description: field.description
          }))
        }));
        setFieldGroups(groups);
      } else {
        // Fallback to hardcoded values
        const groups = [
          {
            name: 'Key Data',
            fields: [
              { name: 'KD_sku', description: 'Part SKU - part identifying number' },
              { name: 'KD_name', description: 'Part Name - name of the part' },
              { name: 'KD_desc', description: 'Part Description - short description of the part' },
              { name: 'KD_cat', description: 'Part Category - category of part' },
              { name: 'KD_curcost', description: 'Part Current Cost (USD)' },
              { name: 'KD_projcost', description: 'Part Projected Cost (USD)' },
              { name: 'KD_curlead', description: 'Part Current Lead Time (Days)' },
              { name: 'KD_projlead', description: 'Part Projected Lead Time (Days)' }
            ]
          }
        ];
        setFieldGroups(groups);
      }
    } catch (error) {
      logger.error('Error loading field mappings:', error);
      setLocalError('Error loading field definitions');
    }
  }, [dispatch, fieldMappingsData]);

  // Fetch suggested mappings on component mount
  useEffect(() => {
    if (!currentFile || !currentFile.fileID) {
      setLocalError('No file selected');
      setLoading(false);
      return;
    }

    // Load field mappings
    loadFieldMappings();

    // Only fetch suggested mappings once
    if (!fetchedMappings && (!columnMappings.suggested || Object.keys(columnMappings.suggested).length === 0)) {
      setFetchedMappings(true); // Set flag to prevent multiple fetches
      
      dispatch(fetchSuggestedMappings(currentFile.fileID))
        .unwrap()
        .then((suggestedMappings) => {
          setLocalMappings(suggestedMappings || {});
          setLoading(false);
        })
        .catch((error) => {
          logger.error('Error fetching suggested mappings:', error);
          setLocalMappings({});
          setLocalError('Could not fetch suggested mappings automatically. Please map columns manually.');
          setLoading(false);
        });
    } else if (columnMappings.suggested && Object.keys(columnMappings.suggested).length > 0) {
      setLocalMappings(columnMappings.suggested);
      setLoading(false);
    }
  }, [dispatch, currentFile, columnMappings.suggested, fieldMappingsData, loadFieldMappings, fetchedMappings]);

  // Handle mapping change
  const handleMappingChange = (fieldName, columnName) => {
    const existingField = Object.keys(localMappings).find(
      key => key !== fieldName && localMappings[key] === columnName && columnName !== 'Use default values'
    );

    if (existingField && columnName !== 'Use default values') {
      dispatch(setError(`The column "${columnName}" is already mapped to field "${existingField}". Please select another column.`));
      return;
    }

    dispatch(clearError());

    setLocalMappings(prev => ({
      ...prev,
      [fieldName]: columnName
    }));
  };

  // Confirm mappings and proceed to next step
  const handleConfirmMappings = () => {
    if (!currentFile || !currentFile.fileID) {
      setLocalError('No file selected');
      return;
    }

    const mappedColumns = Object.values(localMappings).filter(col => col !== 'Use default values');
    const uniqueMappedColumns = new Set(mappedColumns);

    if (mappedColumns.length !== uniqueMappedColumns.size) {
      dispatch(setError('Duplicate mappings detected. Please ensure each column is mapped to only one field.'));
      return;
    }

    dispatch(clearError());

    dispatch(confirmColumnMappings({
      fileID: currentFile.fileID,
      confirmedMappings: localMappings
    }))
      .unwrap()
      .then(() => {
        logger.info('Column mappings confirmed successfully');
      })
      .catch((error) => {
        logger.error('Error confirming column mappings:', error);
        setLocalError('Error confirming column mappings');
      });
  };

  if (loading || progress.status === 'loading') {
    return <div className="loading-indicator">Loading column mapping...</div>;
  }

  const columnOptions = [
    { value: 'Use default values', label: 'Use default values' },
    ...(extractedColumns || []).filter(col => col !== null && col !== undefined)
      .map(col => ({
        value: col,
        label: col
      }))
  ];

  return (
    <div className="column-mapping-section">
      <h2>Map File Columns to System Fields</h2>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="mapping-instructions">
        <p>Map each system field to a column from your file. Fields with no matching columns can use default values.</p>
      </div>

      <div className="field-groups">
        {fieldGroups.map((group, index) => (
          <FieldGroup 
            key={index}
            name={group.name}
            fields={group.fields}
            columnOptions={columnOptions}
            mappings={localMappings}
            onMappingChange={handleMappingChange}
          />
        ))}
      </div>

      <div className="mapping-actions">
        <button 
          className="primary-button"
          onClick={handleConfirmMappings}
          disabled={progress.status === 'loading'}
        >
          {progress.status === 'loading' ? 'Confirming...' : 'Confirm Mappings'}
        </button>
      </div>
    </div>
  );
};

export default ColumnMapping;